import { getDateWithoutTimezoneShift } from '@lender/utils';
import dayjs from 'dayjs';
import _ from 'lodash';

import { DAYS_OF_WEEK } from 'constants/date';
import {
  HelocPurposeType,
  LoanPurposeType,
  OccupancyType,
  PurposeOfChattel,
} from 'constants/loanApplication';
import { interestCreditDefaultTrueProgramName } from 'constants/loanPrepaids';
import { texasState } from 'constants/states';

import {
  checkIsAdditionalLoanFlow,
  checkIsChattelLoanPurpose,
  checkIsHelocLoanType,
  checkIsRefinanceFlow,
} from './loanApplication';

export const WeekdayToStringMap = Object.fromEntries(
  DAYS_OF_WEEK.map((day, index) => [index, day]),
);

const RESCISSION_DAYS_TO_ADD = 3;
const SATURDAY_INDEX = 6;
const SATURDAY = WeekdayToStringMap[SATURDAY_INDEX];
const MAX_BUSINESS_DAY_ITERATIONS = 365;
const HELOC_FIRST_PAYMENT_DEFERRAL_FUNDING_DAY = 15;
// Chattel loans fund weeks after docs and are paid monthly from funding, not from the 1st (LEN-198).
const CHATTEL_FIRST_PAYMENT_DAYS_AFTER_FUNDING = 30;

const getCloseAtDate = ({ closeAt, withTimezoneShift = false }) => {
  const dateByTimezone = withTimezoneShift ? closeAt : getDateWithoutTimezoneShift(closeAt);

  return closeAt ? dayjs(dateByTimezone) : undefined;
};

const skipToWorkingDay = ({ date, businessDays, workingDays }) => {
  if (!date || !businessDays) {
    return date;
  }

  const { internalHolidays, globalHolidays } = businessDays;

  const allHolidays = [...(internalHolidays ?? []), ...(globalHolidays ?? [])];

  const getNextDay = (nextDay, iterations = 0) => {
    if (iterations >= MAX_BUSINESS_DAY_ITERATIONS) {
      return nextDay;
    }

    const isDayHoliday = allHolidays.some(day => dayjs(day.date).isSame(nextDay, 'day'));
    const isDayWeekend =
      workingDays &&
      workingDays.length > 0 &&
      !workingDays.includes(WeekdayToStringMap[nextDay.day()]);
    const isDayHolidayOrWeekend = isDayHoliday || isDayWeekend;

    return isDayHolidayOrWeekend ? getNextDay(nextDay.add(1, 'day'), iterations + 1) : nextDay;
  };

  return getNextDay(dayjs(date));
};

// AlwaysCountSaturdayForFunding widens only the funding calendar; rescission and disbursement stay
// on the Precise days. An empty Precise week already allows every day, so it is left empty.
export const getFundingBusinessDays = ({ preciseBusinessDays, countSaturdayForFunding } = {}) =>
  countSaturdayForFunding && preciseBusinessDays?.length
    ? _.union(preciseBusinessDays, [SATURDAY])
    : preciseBusinessDays;

const skipDaysHolidayOrWeekend = ({ date, businessDays }) =>
  skipToWorkingDay({
    date,
    businessDays,
    workingDays: getFundingBusinessDays(businessDays ?? {}),
  });

// Mirrors Pricing's PrepaidsCalculator.CalculateInterestCreditDisbursementDate: money moves on a
// Precise business day, so a Saturday the flag allowed as the funding date disburses on the next
// one. Prepaid interest, interest credit and the first payment all follow this date.
export const getDefaultDisbursementDate = (fundingDate, businessDays) => {
  if (
    !fundingDate ||
    !businessDays?.countSaturdayForFunding ||
    dayjs(fundingDate).day() !== SATURDAY_INDEX
  ) {
    return fundingDate;
  }

  return skipToWorkingDay({
    date: fundingDate,
    businessDays,
    workingDays: businessDays.preciseBusinessDays,
  });
};

// Pricing treats moving funding onto a day that disburses on the same date (Saturday -> Monday) as
// no change, so the first payment date the user may have entered is kept. A funding date that did
// not move, or one that disburses differently, still re-defaults as before.
export const checkIsSameDisbursementForNewFundingDate = ({
  prevFundingDate,
  fundingDate,
  businessDays,
}) =>
  !_.isNil(prevFundingDate) &&
  !dayjs(prevFundingDate).isSame(fundingDate, 'day') &&
  dayjs(getDefaultDisbursementDate(dayjs(prevFundingDate), businessDays)).isSame(
    getDefaultDisbursementDate(dayjs(fundingDate), businessDays),
    'day',
  );

// A disbursement that is only the default for a Saturday funding date must not be copied back onto
// funding, or the Saturday would move to the following Monday.
export const checkIsDefaultDisbursementForFundingDate = ({
  fundingDate,
  disbursementDate,
  businessDays,
}) =>
  !_.isNil(fundingDate) &&
  !dayjs(fundingDate).isSame(disbursementDate, 'day') &&
  dayjs(getDefaultDisbursementDate(dayjs(fundingDate), businessDays)).isSame(
    disbursementDate,
    'day',
  );

// A chattel refinance of the borrower's home is rescindable like a retail refinance; only an
// explicit chattel Purchase is not, so a purpose not yet captured keeps the rescission period.
export const checkIsChattelRescindable = loanApplication =>
  checkIsChattelLoanPurpose(loanApplication?.application?.loanPurpose) &&
  loanApplication?.application?.purposeOfChattel !== PurposeOfChattel.PURCHASE;

export const getFundingDateValueByCreditNoteDate = ({
  loanApplication,
  businessDays,
  interestCreditNoteDate,
  subjectPropertyOccupancy,
}) => {
  const loanPurpose = loanApplication.application?.loanPurpose;
  const isLoanPurposeRefinance = loanPurpose === LoanPurposeType.REFINANCE;
  const isChattelRescindable = checkIsChattelRescindable(loanApplication);

  const isOccupancyPrimary = subjectPropertyOccupancy === OccupancyType.PRIMARY_RESIDENCE;

  if (
    (isLoanPurposeRefinance || isChattelRescindable) &&
    isOccupancyPrimary &&
    interestCreditNoteDate
  ) {
    return getRescindableFundingDate({ noteDate: interestCreditNoteDate, businessDays });
  }

  return skipDaysHolidayOrWeekend({
    date: interestCreditNoteDate,
    businessDays,
  });
};

// Funding follows rescission expiry by one funding day, as Pricing computes it; counting both in
// one pass over the Precise days only agrees while rescission and funding share a calendar.
const getRescindableFundingDate = ({ noteDate, businessDays }) => {
  const rescissionDate = addPresiceBusinessDays({
    date: noteDate,
    businessDays,
    days: RESCISSION_DAYS_TO_ADD,
  });

  if (!businessDays?.preciseBusinessDays?.length) {
    return rescissionDate;
  }

  return getNextBusinessDay({ date: rescissionDate, businessDays });
};

const addPresiceBusinessDays = ({ date, businessDays, days }) => {
  if (!businessDays) {
    return date;
  }

  const { internalHolidays, globalHolidays, preciseBusinessDays } = businessDays;

  // The working week is what makes a day countable; holidays only remove further days.
  if (!preciseBusinessDays || !preciseBusinessDays.length) {
    return date;
  }

  const allHolidays = [...(internalHolidays ?? []), ...(globalHolidays ?? [])];

  const getNextDay = (nextDay, daysLeft, iterations = 0) => {
    if (iterations >= MAX_BUSINESS_DAY_ITERATIONS) {
      return nextDay;
    }

    const isDayHolidayOrWeekend =
      allHolidays.some(day => dayjs(day.date).isSame(nextDay, 'day')) ||
      !preciseBusinessDays.includes(WeekdayToStringMap[nextDay.day()]);

    if (daysLeft === 0) {
      return isDayHolidayOrWeekend
        ? getNextDay(nextDay.add(1, 'day'), daysLeft, iterations + 1)
        : nextDay;
    }

    if (isDayHolidayOrWeekend) {
      return getNextDay(nextDay.add(1, 'day'), daysLeft, iterations + 1);
    }

    return getNextDay(nextDay.add(1, 'day'), daysLeft - 1, iterations + 1);
  };

  return getNextDay(dayjs(date).add(1, 'day'), days - 1);
};

export const getDefaultInterestCreditValue = ({
  loanApplication,
  initialHasInterestCredit,
  subjectPropertyState,
}) => {
  if (!_.isNil(initialHasInterestCredit)) {
    return initialHasInterestCredit;
  }

  const { internalName } = loanApplication.application?.program || {};

  const isStateTexas = subjectPropertyState === texasState[1];
  const isProgramNameIncludesSpecificTitle = _.toUpper(internalName).includes(
    interestCreditDefaultTrueProgramName,
  );

  return isStateTexas && isProgramNameIncludesSpecificTitle;
};

export const getRescisionDateValue = ({
  loanApplication,
  businessDays,
  interestCreditNoteDate,
  subjectPropertyOccupancy,
}) => {
  const helocPurpose = loanApplication.application?.helocPurpose;
  const loanPurpose = loanApplication.application?.loanPurpose;

  const isLoanPurposeRefinance = checkIsRefinanceFlow(loanPurpose);
  const isLoanPurposeAdditional = checkIsAdditionalLoanFlow(loanPurpose);
  const isHelocPurposeNotPurchase = helocPurpose !== HelocPurposeType.HOME_PURCHASE;

  const isOccupancyPrimary = subjectPropertyOccupancy === OccupancyType.PRIMARY_RESIDENCE;

  if (
    isOccupancyPrimary &&
    interestCreditNoteDate &&
    (isLoanPurposeRefinance ||
      (isLoanPurposeAdditional && isHelocPurposeNotPurchase) ||
      checkIsChattelRescindable(loanApplication))
  ) {
    return addPresiceBusinessDays({
      date: interestCreditNoteDate,
      businessDays,
      days: RESCISSION_DAYS_TO_ADD,
    });
  }

  return undefined;
};

export const getNextBusinessDay = ({ date, businessDays }) => {
  if (!date) {
    return;
  }

  return skipDaysHolidayOrWeekend({
    date: dayjs(date).add(1, 'day'),
    businessDays,
  });
};

const getHelocFirstPaymentMonthsToAdd = fundingDate =>
  dayjs(fundingDate).date() < HELOC_FIRST_PAYMENT_DEFERRAL_FUNDING_DAY ? 1 : 2;

const getInterestCreditFirstPaymentMonthsToAdd = hasInterestCredit => (hasInterestCredit ? 1 : 2);

// A HELOC's first payment is keyed to the funding day, not to interest credit; this mirrors
// the server's PrepaidsCalculator.CalculateHelocFirstPaymentDate so both defaults agree.
// A chattel first payment falls a fixed number of days after funding, on any day of the month,
// mirroring PrepaidsCalculator.CalculateChattelFirstPaymentDate.
export const getDefaultInterestCreditFirstPaymentDate = ({
  hasInterestCredit,
  interestCreditFundingDate,
  isHeloc = false,
  isChattel = false,
}) => {
  if (interestCreditFundingDate && !dayjs(interestCreditFundingDate).isValid()) {
    return;
  }

  if (isChattel) {
    return interestCreditFundingDate
      ? dayjs(interestCreditFundingDate).add(CHATTEL_FIRST_PAYMENT_DAYS_AFTER_FUNDING, 'day')
      : undefined;
  }

  const monthsToAdd = isHeloc
    ? getHelocFirstPaymentMonthsToAdd(interestCreditFundingDate)
    : getInterestCreditFirstPaymentMonthsToAdd(hasInterestCredit);

  return dayjs(interestCreditFundingDate).add(monthsToAdd, 'month').startOf('month');
};

// A chattel funding date the user typed on or after the earliest allowed date (the day after
// rescission, or the note date) survives a note or closing date change; an earlier one is pulled
// up to that floor. Without a floor (no note date) the current funding date is kept.
export const getChattelFundingDate = ({ currentFundingDate, floorDate }) => {
  if (!currentFundingDate) {
    return floorDate;
  }

  if (!floorDate || !dayjs(currentFundingDate).isBefore(floorDate, 'day')) {
    return dayjs(currentFundingDate);
  }

  return floorDate;
};

export const checkIsHelocLoanApplication = loanApplication =>
  checkIsHelocLoanType(loanApplication?.application?.loanType);

export const checkIsChattelLoanApplication = loanApplication =>
  checkIsChattelLoanPurpose(loanApplication?.application?.loanPurpose);

const getInterestCreditNoteDate = ({ interestCredit, closeAt, withTimezoneShift = false }) => {
  if (interestCredit.interestCreditNoteDate === null) {
    return null;
  }

  const dateByTimezone = withTimezoneShift
    ? interestCredit.interestCreditNoteDate
    : getDateWithoutTimezoneShift(interestCredit.interestCreditNoteDate);

  return interestCredit.interestCreditNoteDate
    ? dayjs(dateByTimezone)
    : getCloseAtDate({ closeAt, withTimezoneShift });
};

export const getInterestCreditFundingDate = ({
  interestCredit,
  loanApplication,
  businessDays,
  closeAt,
  withTimezoneShift = false,
  subjectPropertyOccupancy,
}) => {
  const dateByTimezone = withTimezoneShift
    ? interestCredit.interestCreditFundingDate
    : getDateWithoutTimezoneShift(interestCredit.interestCreditFundingDate);

  return interestCredit.interestCreditFundingDate
    ? dayjs(dateByTimezone)
    : getFundingDateValueByCreditNoteDate({
        loanApplication,
        businessDays,
        interestCreditNoteDate: getInterestCreditNoteDate({
          interestCredit,
          closeAt,
          withTimezoneShift,
        }),
        subjectPropertyOccupancy,
      });
};

const getInitialFirstPaymentDate = ({
  hasInterestCredit,
  fundingDate,
  firstPaymentDate,
  isHeloc,
  isChattel,
}) => {
  if (firstPaymentDate) {
    return firstPaymentDate;
  }

  if (!fundingDate) {
    return;
  }

  return getDefaultInterestCreditFirstPaymentDate({
    hasInterestCredit,
    interestCreditFundingDate: fundingDate,
    isHeloc,
    isChattel,
  });
};

export const getInterestCreditValues = ({
  loanApplication,
  interestCredit,
  closeAt,
  businessDays,
  subjectPropertyOccupancy,
  subjectPropertyState,
}) => {
  const hasInterestCredit = getDefaultInterestCreditValue({
    loanApplication,
    initialHasInterestCredit: interestCredit.hasInterestCredit,
    subjectPropertyState,
  });

  const interestCreditNoteDate = getInterestCreditNoteDate({ interestCredit, closeAt });

  const interestCreditFundingDate = getInterestCreditFundingDate({
    interestCredit,
    loanApplication,
    businessDays,
    closeAt,
    subjectPropertyOccupancy,
  });

  const interestCreditFirstPaymentDate = interestCredit.interestCreditFirstPaymentDate
    ? dayjs(getDateWithoutTimezoneShift(interestCredit.interestCreditFirstPaymentDate))
    : undefined;

  const interestCreditDisbursementDate = interestCredit.interestCreditDisbursementDate
    ? dayjs(getDateWithoutTimezoneShift(interestCredit.interestCreditDisbursementDate))
    : getDefaultDisbursementDate(interestCreditFundingDate, businessDays);

  const interestCreditRescissionDate = getRescisionDateValue({
    businessDays,
    loanApplication,
    interestCreditNoteDate: getInterestCreditNoteDate({
      interestCredit,
      closeAt,
    }),
    subjectPropertyOccupancy,
  });

  const initialInterestCreditFirstPaymentDate = getInitialFirstPaymentDate({
    hasInterestCredit,
    isHeloc: checkIsHelocLoanApplication(loanApplication),
    isChattel: checkIsChattelLoanApplication(loanApplication),
    firstPaymentDate: interestCreditFirstPaymentDate,
    fundingDate: getDefaultDisbursementDate(interestCreditFundingDate, businessDays),
  });

  return {
    hasInterestCredit,

    // unshifted dates
    interestCreditNoteDate,
    interestCreditFundingDate,
    interestCreditDisbursementDate,
    interestCreditFirstPaymentDate: initialInterestCreditFirstPaymentDate,
    interestCreditRescissionDate,
  };
};
